import { TableFilterModel } from "models/TableFilterModel";
import { BaseService } from "./base.service";
import {
	CompanyAddressDetailModel,
	CompanyBankAccountDetailModel,
	CompanyDirectorDetailModel,
	CompanyProfileDetailModel,
	IAddressTabGridDetail,
	ICity,
	ICompanyBankAccountTabGridDetail,
	ICompanyDirectorTabGridDetail,
	ICompanyDocumentTabGridDetail,
	ICountry,
	IMunicipality,
	IProvince,
	ISupplierAddress,
	ISupplierBankAccountTypeDetail,
	ISupplierBankDetail,
	IUserTabGridDetail,
	SupplierAddressDetailModel,
	SupplierCompanyDirectorDetailModel,
	SupplierCompanyDocumentDetail,
	SupplierUserDetailModel,
} from "models/VendorPortal/companyProfileDetail";

class CompanyProfileServiceInternal extends BaseService {
	// SupplierCompanyDirector
	getSupplierCompanyDirectorList = async (
		model: {
			sortBy: string;
			sortType: string;
			pageNo: number;
			pageSize: number;
		},
		supplierId: number
	): Promise<ICompanyDirectorTabGridDetail[]> =>
		this.callApi<ICompanyDirectorTabGridDetail[]>(
			`SupplierCompanyDirector/GetSupplierCompanyDirectorList/${supplierId}`,
			"POST",
			undefined,
			model
		);

	getSupplierCompanyDirectorDetail = async (
		companyDirectorId: number
	): Promise<CompanyDirectorDetailModel> =>
		this.callApi<CompanyDirectorDetailModel>(
			`SupplierCompanyDirector/GetSupplierCompanyDirectory/${companyDirectorId}`,
			"GET"
		);

	getSupplierCompanyDirector = async (
		supplierCompanyDirectorId: number
	): Promise<SupplierCompanyDirectorDetailModel> =>
		this.callApi<SupplierCompanyDirectorDetailModel>(
			`SupplierCompanyDirector/GetSupplierCompanyDirectoryDetails/${supplierCompanyDirectorId}`,
			"GET"
		);

	saveSupplierCompanyDirectorDetail = async (
		model: CompanyDirectorDetailModel
	): Promise<undefined> =>
		this.callApi<undefined>(
			`SupplierCompanyDirector/Save`,
			"POST",
			undefined,
			model
		);

	updateSupplierCompanyDirectorDetail = async (
		model: CompanyDirectorDetailModel
	): Promise<undefined> =>
		this.callApi<undefined>(
			`SupplierCompanyDirector/Update/${model.supplierCompanyDirectorId}`,
			"PUT",
			undefined,
			model
		);

	deleteCompanyDirectorDetail = async (
		companyDirectorId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`SupplierCompanyDirector/Delete/${companyDirectorId}`,
			"DELETE"
		);
	// SupplierCompanyDirector - END

	// SupplierCompanyDocument
	getSupplierCompanyDocumentList = async (
		model: {
			sortBy: string;
			sortType: string;
			pageNo: number;
			pageSize: number;
		},
		supplierId: number
	): Promise<ICompanyDocumentTabGridDetail[]> =>
		this.callApi<ICompanyDocumentTabGridDetail[]>(
			`SupplierCompanyDocument/GetSupplierCompanyDocumentList/${supplierId}`,
			"POST",
			undefined,
			model
		);

	saveSupplierCompanyDocumentDetail = async (
		model: FormData
	): Promise<undefined> =>
		this.callApi<undefined>(
			`SupplierCompanyDocument/Save`,
			"POST",
			undefined,
			model,
			undefined,
			true
		);

	deleteCompanyDocumentDetail = async (
		documentId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`SupplierCompanyDocument/Delete/${documentId}`,
			"DELETE"
		);

	getSupplierCompanyDocumentDetails = async (
		supplierCompanyDocumentId: number
	): Promise<SupplierCompanyDocumentDetail> =>
		this.callApi<SupplierCompanyDocumentDetail>(
			`SupplierCompanyDocument/GetSupplierCompanyDocumentDetails/${supplierCompanyDocumentId}`,
			"GET"
		);

	getSupplierDocumentTypeList = async (): Promise<
		{ supplierDocumentTypeId: number; name: string }[]
	> =>
		this.callApi<{ supplierDocumentTypeId: number; name: string }[]>(
			`SupplierDocumentType/GetSupplierDocumentTypeList`,
			"GET",
			undefined,
			undefined,
			undefined,
			undefined,
			false
		);
	// SupplierCompanyDocument - END

	// SupplierBankAccounts
	getSupplierCompanyBankAccountList = async (
		supplierId: number,
		model: TableFilterModel
	): Promise<ICompanyBankAccountTabGridDetail[]> =>
		this.callApi<ICompanyBankAccountTabGridDetail[]>(
			`SupplierBanks/GetSupplierBankList/${supplierId}`,
			"POST",
			undefined,
			model
		);

	getSupplierCompanyBankAccountDetail = async (
		supplierBankId: number
	): Promise<CompanyBankAccountDetailModel> =>
		this.callApi<CompanyBankAccountDetailModel>(
			`SupplierBanks/GetSupplierBank/${supplierBankId}`,
			"GET"
		);

	saveSupplierCompanyBankAccountDetail = async (
		model: CompanyBankAccountDetailModel
	): Promise<{ supplierBankId: number }> =>
		this.callApi<{ supplierBankId: number }>(
			`SupplierBanks/Save`,
			"POST",
			undefined,
			model
		);

	updateSupplierCompanyBankAccountDetail = async (
		model: CompanyBankAccountDetailModel
	): Promise<number> =>
		this.callApi<number>(
			`SupplierBanks/Update/${model.supplierBankId}`,
			"PUT",
			undefined,
			model
		);

	deleteCompanyBankAccountDetail = async (
		supplierBankId: number
	): Promise<undefined> =>
		this.callApi<undefined>(`SupplierBanks/Delete/${supplierBankId}`, "DELETE");

	uploadBankAccountDocument = async (
		supplierBankId: number,
		formData: FormData
	): Promise<{
		supplierBankId: number;
		documentFileName: string;
		documentFilePath: string;
	}> =>
		this.callApi<{
			supplierBankId: number;
			documentFileName: string;
			documentFilePath: string;
		}>(
			`SupplierBanks/UploadBankAccountDocument/${supplierBankId}`,
			"POST",
			undefined,
			formData,
			undefined,
			true
		);

	getSupplierCompanyBankList = async (): Promise<ISupplierBankDetail[]> =>
		this.callApi<ISupplierBankDetail[]>(
			`Bank/GetBankList`,
			"GET",
			undefined,
			undefined,
			undefined,
			undefined,
			false
		);

	getSupplierBankAccountTypeList = async (): Promise<
		ISupplierBankAccountTypeDetail[]
	> =>
		this.callApi<ISupplierBankAccountTypeDetail[]>(
			`BankAccountType/GetBankAccountTypeList`,
			"GET",
			undefined,
			undefined,
			undefined,
			undefined,
			false
		);
	// SupplierBankAccounts - END

	// Company Profile Header
	getSupplierCompanyProfileDetail = async (
		supplierId: number
	): Promise<CompanyProfileDetailModel> =>
		this.callApi<CompanyProfileDetailModel>(
			`SupplierCompanyProfile/GetSupplierCompanyProfile/${supplierId}`,
			"GET"
		);

	updateSupplierCompanyProfileDetail = async (model: {
		supplierId: number;
		isVatRegistered: boolean;
		vatNumber: string;
		isCsdRegistered: boolean;
		maaaCsdNumber: string;
		csdStatusId: number | null;
		remitTo: string | null;
	}): Promise<undefined> =>
		this.callApi<undefined>(
			`SupplierCompanyProfile/UpdateSupplierCompanyProfile/${model.supplierId}`,
			"POST",
			undefined,
			model
		);

	getCsdEntityTypesList = async (
		isCsdRegistered = true
	): Promise<{ csdEntityTypeId: number; name: string }[]> =>
		this.callApi<{ csdEntityTypeId: number; name: string }[]>(
			`CsdEntityTypes/GetCsdEntityTypesList/${isCsdRegistered}`,
			"GET"
		);

	getCsdStatusList = async (): Promise<
		{ csdStatusId: number; name: string }[]
	> =>
		this.callApi<{ csdStatusId: number; name: string }[]>(
			`CsdStatus/GetCsdStatusList`,
			"GET"
		);
	// Company Profile Header - END

	// Supplier Address
	getSupplierAddressList = async (
		model: {
			sortBy: string;
			sortType: string;
			pageNo: number;
			pageSize: number;
		},
		supplierId: number
	): Promise<IAddressTabGridDetail[]> =>
		this.callApi<IAddressTabGridDetail[]>(
			`SupplierAddress/GetSupplierAddressList/${supplierId}`,
			"POST",
			undefined,
			model
		);

	getSupplierAddress = async (
		supplierAddressId: number
	): Promise<CompanyAddressDetailModel> =>
		this.callApi<CompanyAddressDetailModel>(
			`SupplierAddress/GetSupplierAddress/${supplierAddressId}`,
			"GET"
		);

	getSupplierAddressDetail = async (
		supplierAddressId: number
	): Promise<SupplierAddressDetailModel> =>
		this.callApi<SupplierAddressDetailModel>(
			`SupplierAddress/GetSupplierAddressDetails/${supplierAddressId}`,
			"GET"
		);

	saveSupplierAddressDetail = async (
		model: CompanyAddressDetailModel
	): Promise<{ supplierAddressId: number }> =>
		this.callApi<{ supplierAddressId: number }>(
			`SupplierAddress/Save`,
			"POST",
			undefined,
			model
		);

	updateSupplierAddressDetail = async (
		model: CompanyAddressDetailModel
	): Promise<{ supplierAddressId: number; isAlreadyExist: boolean }> =>
		this.callApi<{ supplierAddressId: number; isAlreadyExist: boolean }>(
			`SupplierAddress/Update/${model.supplierAddressId}`,
			"PUT",
			undefined,
			model
		);

	deleteSupplierAddressDetail = async (
		supplierAddressId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`SupplierAddress/Delete/${supplierAddressId}`,
			"DELETE"
		);

	getCountryList = async (): Promise<ICountry[]> =>
		this.callApi<ICountry[]>(`Country/GetCountryList`, "GET");

	getProvinceList = async (countryId: number): Promise<IProvince[]> =>
		this.callApi<IProvince[]>(`Province/GetProvinceList/${countryId}`, "GET");

	getAllProvinceList = async (): Promise<IProvince[]> =>
		this.callApi<IProvince[]>(`Province/GetAll`, "GET");

	getMunicipalityList = async (provinceIds: string): Promise<IMunicipality[]> =>
		this.callApi<IMunicipality[]>(
			`Municipality/GetMunicipalityList/${provinceIds}`,
			"GET"
		);

	getCityList = async (municipalityId: number): Promise<ICity[]> =>
		this.callApi<ICity[]>(`City/GetCityList/${municipalityId}`, "GET");

	getSupplierAddressListBySupplier = async (
		supplierId: number
	): Promise<ISupplierAddress[]> =>
		this.callApi<ISupplierAddress[]>(
			`SupplierAddress/GetSupplierAddressListBySupplierId/${supplierId}`,
			"GET"
		);

	// SupplierAddress - END

	// Supplier User
	getSupplierUserList = async (
		model: {
			sortBy: string;
			sortType: string;
			pageNo: number;
			pageSize: number;
			filterField: string;
		},
		supplierId: number,
		isAdmin: boolean
	): Promise<IUserTabGridDetail[]> =>
		this.callApi<IUserTabGridDetail[]>(
			`SupplierUser/GetUserSupplierGridList/${supplierId}`,
			"POST",
			new URLSearchParams({
				isAdmin: isAdmin.toString(),
			}),
			model
		);

	getSupplierUser = async (userId: number): Promise<SupplierUserDetailModel> =>
		this.callApi<SupplierUserDetailModel>(
			`SupplierUser/GetUserSupplier/${userId}`,
			"GET"
		);

	saveSupplierUserDetail = async (
		model: SupplierUserDetailModel
	): Promise<number> =>
		this.callApi<number>(
			`SupplierUser/SaveExternalUser`,
			"POST",
			undefined,
			model
		);

	updateSupplierUserDetail = async (
		model: SupplierUserDetailModel
	): Promise<number> =>
		this.callApi<number>(
			`SupplierUser/UpdateExternalUser/${model.userId}`,
			"PUT",
			undefined,
			model
		);

	deleteSupplierUserDetail = async (userId: number): Promise<undefined> =>
		this.callApi<undefined>(
			`SupplierUser/DeleteExternalUser/${userId}`,
			"DELETE"
		);

	updateExternalUserStatus = async (
		userId: number,
		userStatus: boolean
	): Promise<boolean> =>
		this.callApi<boolean>(
			`SupplierUser/UpdateExternalUserStatus/${userId}/${userStatus}`,
			"POST"
		);

	isSupplierUserExist = async (supplierId: number): Promise<boolean> =>
		this.callApi<boolean>(
			`SupplierUser/IsSupplierUserExist/${supplierId}`,
			"GET",
			undefined,
			undefined,
			false,
			false,
			false
		);
	// Supplier user - END
}

export const CompanyProfileService = new CompanyProfileServiceInternal();
